import { dbService, storageService } from '../firebaseInstance';
import { getAllCollection } from '../utils/pureDataFetch';

/**
 * 컬렉션을 작성한 사용자 목록을 가져옵니다.
 * @returns {Promise<Array>} 컬렉션을 작성한 사용자 목록
 */
export const getAllUsers = async () => {
  try {
    console.log(' 컬렉션 작성자 목록을 가져오는 중...');
    
    // 1. 모든 컬렉션을 불러오고
    const allCollections = await getAllCollection();
    console.log(` 총 ${allCollections.length}개의 컬렉션 발견`);
    
    // 2. 컬렉션 작성자 ID 추출 (중복 제거)
    const authorIds = [...new Set(allCollections.map(collection => collection.userID).filter(Boolean))];
    console.log(`👥 컬렉션 작성자 ${authorIds.length}명 발견`);
    
    if (authorIds.length === 0) {
      console.log('⚠️ 컬렉션 작성자가 없습니다');
      return [];
    }
    
    // 3. 컬렉션 작성자들의 상세 정보 가져오기
    const usersData = [];
    
    for (const authorId of authorIds) {
      try {
        const userDoc = await dbService.collection("user").doc(authorId).get();
        
        if (userDoc.exists) {
          const userData = userDoc.data();
          
          // 필수 필드 검증
          if (userData && userData.profile) {
            // 필수 프로필 필드 확인
            const hasRequiredFields = userData.profile.id && userData.profile.username;
            
            if (hasRequiredFields) {
              // 해당 사용자가 작성한 컬렉션 수 계산
              const userCollections = allCollections.filter(collection => collection.userID === authorId);
              
              // 아바타 존재 여부 확인 (Firebase Storage Public URL 방식)
              const hasAvatar = await checkUserAvatarExists(authorId);
              
              usersData.push({
                id: authorId,
                ...userData,
                collectionCount: userCollections.length,
                collections: userCollections.map(c => ({ id: c.id, title: c.title })),
                hasAvatar: hasAvatar
              });
            }
          }
        }
      } catch (error) {
        console.warn(`사용자 ${authorId} 정보 가져오기 실패:`, error);
        continue;
      }
    }

    console.log(`✅ ${usersData.length}명의 컬렉션 작성자 정보 로드 완료`);
    return usersData;
  } catch (error) {
    console.error('Get collection authors failed:', error);
    return [];
  }
};

/**
 * 특정 사용자의 아바타 존재 여부를 확인합니다.
 * @param {string} userID - 사용자 ID
 * @returns {Promise<boolean>} 아바타 존재 여부
 */
export const checkUserAvatarExists = async (userID) => {
  try {
    // Firebase Storage Public URL 방식으로 확인
    const storageBucket = process.env.REACT_APP_STORAGE_BUCKET;
    const encodedPath = encodeURIComponent(`avatars/${userID}.jpg`);
    const publicUrl = `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${encodedPath}?alt=media`;
    
    // HEAD 요청으로 파일 존재 여부 확인
    const response = await fetch(publicUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    // 모든 에러를 false로 처리하여 무한 로딩 방지
    return false;
  }
};

/**
 * 아바타가 없는 사용자 목록을 가져옵니다.
 * @returns {Promise<Array>} 아바타 없는 사용자 목록
 */
export const getUsersWithoutAvatar = async () => {
  try {
    const allUsers = await getAllUsers();
    return allUsers.filter(user => !user.hasAvatar);
  } catch (error) {
    console.error('Get users without avatar failed:', error);
    return [];
  }
};

/**
 * 사용자를 검색합니다.
 * @param {string} query - 검색어 (사용자명 또는 ID)
 * @returns {Promise<Array>} 검색 결과
 */
export const searchUsers = async (query) => {
  try {
    const allUsers = await getAllUsers();
    const lowerQuery = query.toLowerCase();
    
    return allUsers.filter(user => {
      const username = user.profile?.username?.toLowerCase() || '';
      const name = user.profile?.name?.toLowerCase() || '';
      const id = user.profile?.id?.toLowerCase() || '';
      
      return username.includes(lowerQuery) || 
             name.includes(lowerQuery) || 
             id.includes(lowerQuery);
    });
  } catch (error) {
    console.error('Search users failed:', error);
    return [];
  }
};

/**
 * 사용자별 아바타 상태를 일괄 확인합니다.
 * @param {Array} users - 사용자 목록
 * @returns {Promise<Array>} 아바타 상태가 포함된 사용자 목록
 */
export const getUsersWithAvatarStatus = async (users) => {
  try {
    // users가 빈 배열이면 컬렉션 작성자를 가져옴
    const userList = users.length === 0 ? await getAllUsers() : users;
    
    // 이미 hasAvatar 상태가 포함되어 있으므로 그대로 반환
    return userList;
    
  } catch (error) {
    console.error('Get users with avatar status failed:', error);
    return users.map(user => ({
      ...user,
      hasAvatar: false
    }));
  }
};

/**
 * 사용자 아바타 업로드 이력을 가져옵니다.
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 업로드 이력
 */
export const getAvatarUploadHistory = async (userID) => {
  try {
    const userDoc = await dbService.collection("user").doc(userID).get();
    if (!userDoc.exists) return [];
    
    const userData = userDoc.data();
    return userData.avatarUploadHistory || [];
  } catch (error) {
    console.error(`Get avatar upload history failed for ${userID}:`, error);
    return [];
  }
}; 